<template>
  <modal width="560px" :title="t('Apply Promo')" @close="emit('close')">
    <div class="wrapper">
      <div class="wrapper-container">
        <div class="pb-10">
          <ui-icon size="42" :color="$colors.green" icon="fas fa-gift" />
        </div>
        <span>{{ t('Provide the promotional code to increase bonus balance.') }}</span>
        <ui-input
          v-model="promoCode"
          :label="t('Promo Code')"
          :placeholder="t('Promo Code')"
          name="promoCode"
          autofocus
          :error-messages="fieldErrors.promoCode || []"
          @keyup.enter="submit"
        />
        <div class="shiny-text-wrapper" v-if="info.promocode && info.title">
          <div class="shiny-text">{{ info.promocode }}</div>
          <div class="shiny-title">{{ info.title }}</div>
          <div
            class="instruction-text"
            v-html="
              t(
                'Click <b>Apply</b> to activate the promocode and access the <b>Change Plan</b> section to enjoy the deal.',
              )
            "
          ></div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="d-flex align-items-center justify-end">
        <ui-button
          :text="t('Cancel')"
          icon="fas fa-times"
          :disabled="loading"
          @click="emit('close')"
        />

        <ui-button
          :text="t('Apply')"
          icon="fas fa-check"
          button-style="accent"
          :loading="loading"
          @click="submit"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { postApplyBillingPromoApiRequest } from '@/services/api/billing-api';
import { useI18n, useColors } from '@/composables';
import { useApiFieldErrors } from '@/composables/useApiFieldErrors';
import { isFreePlan } from '@/services/utils/billing-format';
import { $emitter } from '@/services/emitter';
import { useStore } from '@/store';

const props = withDefaults(
  defineProps<{
    data?: { promocode?: string };
  }>(),
  {
    data: () => ({}),
  },
);

const emit = defineEmits<{
  close: [];
}>();

const $colors = useColors();
const { t } = useI18n();
const store = useStore();

const promoCode = ref('');
const loading = ref(false);
const { fieldErrors, clearErrors, applyApiErrors } = useApiFieldErrors();

const showCase1 = computed(() => {
  const promoCodesUsed = store.billing.promoCodesUsed;
  return isFreePlan(store.billing) && !promoCodesUsed?.length;
});

const showCase2 = computed(() => {
  const promoCodesUsed = store.billing.promoCodesUsed;
  return isFreePlan(store.billing) && promoCodesUsed?.every(item => item.appliedCount === 0);
});

const info = computed(() => {
  if (showCase1.value) {
    return {
      title: '50% OFF ON ALL PLANS + ADDONS',
      promocode: 'HAPPY50',
    };
  }
  if (showCase2.value) {
    return {
      title: 'USE YOUR DISCOUNT - GET $10 OFF',
      promocode: 'BEHAPPY10',
    };
  }
  return {};
});

const submit = async () => {
  if (!promoCode.value.length) {
    return;
  }
  clearErrors();
  loading.value = true;
  try {
    await postApplyBillingPromoApiRequest({ promoCode: promoCode.value });
    emit('close');
    $emitter.emit('toast-success', t('Promo code successfully applied.'));
  } catch (error: unknown) {
    applyApiErrors(error);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  promoCode.value = props.data?.promocode ?? '';
});
</script>

<style lang="scss" scoped>
.footer {
  display: flex;
  justify-content: flex-end;
}
.wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}
.wrapper-container {
  width: 300px;
  padding: 24px;
}

.shiny-text-wrapper {
  margin-top: 16px;
  text-align: center;
}

.shiny-text {
  font-size: 21px;
  font-weight: 900;
  color: #00ff88;
  letter-spacing: 2.6px;
  text-shadow:
    0 0 17px rgba(0, 255, 136, 0.8),
    0 0 34px rgba(0, 255, 136, 0.6),
    0 4px 7px rgba(0, 0, 0, 0.8);
  font-family: 'Courier New', monospace;
  padding: 7px 13px;
  border: 1px solid rgba(0, 255, 136, 0.5);
  border-radius: 7px;
  background-color: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);
  display: inline-block;
  animation: promoPulse 2s ease-in-out infinite;
}

@keyframes promoPulse {
  0%,
  100% {
    transform: scale(1);
    border-color: rgba(0, 255, 136, 0.5);
  }
  50% {
    transform: scale(1.05);
    border-color: rgba(0, 255, 136, 0.9);
  }
}

.shiny-title {
  margin-top: 8px;
  font-size: 14px;
  color: #000;
  line-height: 1.4;
}
</style>
